import { Spin } from 'antd';
import { Suspense } from 'react';
import { Navigate, useRoutes } from 'react-router-dom';
import { PermissionEn } from '@martech-solution/utils/constants';
import type { IResourceFullAccessDetail } from 'src/types/master_data/userResource.type';
import { getAccessTokenFromLS } from '@martech-solution/utils/utils';
import { Forbidden } from '@martech-solution/utils/pages';
import { signinRedirect } from '@martech-solution/utils/apis';
import { usePermissionNew } from './usePermission';

// Import organized routes
import { allRoutes, routesCheckPermission } from 'src/routes';
import type { RouteType } from 'src/routes';

export default function useRouteElements(resources: IResourceFullAccessDetail[], resourcePermissionLoading: boolean) {
  const permissionResource = usePermissionNew(
    [...routesCheckPermission].map((x) => (!x.path.startsWith('/system-admin') ? `/system-admin${x.path}` : x.path)),

    resources
  );

  const hasPermission = (path: string, permission: PermissionEn) => {
    let checkPath = path;
    if (checkPath?.includes('/system-admin/seasonal-employee')) {
      checkPath = checkPath.replace('/system-admin/seasonal-employee', '/system-admin/employee');
    }

    const similarResource = permissionResource?.filter((x) => checkPath?.includes(x?.path as string));

    const mostSimilarPath = similarResource?.reduce((prevPath, currPath) => {
      return (currPath?.path?.length || 0) > (prevPath?.path?.length || 0) ? currPath : prevPath;
    }, similarResource[0]);
    if (mostSimilarPath) {
      return mostSimilarPath?.permissions?.includes(permission);
    }
    return false;
  };

  // Helper function to create route element with proper error handling and permissions
  const createRouteElement = (route: RouteType) => {
    const { path, redirect, Layout, Component, permission } = route;

    // Handle redirect routes
    if (redirect) {
      return { path, element: <Navigate to={redirect} replace /> };
    }

    // Create the component with permission and path checks
    const createComponent = () => {
      if (!Component) return <></>;

      // Check permissions if required
      if (permission && !hasPermission(path, permission)) {
        return <Forbidden />;
      }

      return <Component />;
    };

    return {
      path,
      element: Layout ? <Layout>{createComponent()}</Layout> : createComponent()
    };
  };
  // Convert routes to route elements
  const routeElements = allRoutes.map((route) => createRouteElement(route));
  // Always call useRoutes hook first to avoid conditional hook call
  const routes = useRoutes(routeElements);

  // Check authentication first
  const token = getAccessTokenFromLS();

  // If no token and not on auth routes, redirect to login
  if (!token && !window.location.pathname.includes('/signin-oidc') && !window.location.pathname.includes('/signout')) {
    signinRedirect(window.location.pathname);
    return;
  }

  // Show loading spinner while resources are loading (only if user is authenticated)
  // if (token && (resourcePermissionLoading || !resources)) {
  //   return <LoadingCustom />;
  // }

  return routes;
}
